import type { ApiClient } from '@carbonwebmail/api-client'
import type { DnsRecordDto, SendingDomainDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { CopyableValue } from '../../ui/CopyableValue.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { EmptyCard, ListScreen } from '../../ui/PagedScreen.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'
import { useMay } from '../../ui/yourRole.js'

import { useSendingDomains } from './useSendingDomains.js'

/**
 * The domains you send from, and whether the DNS agrees.
 *
 * Unverified is visibly blocking here because it is: mail from a domain with no
 * SPF and no DKIM goes to spam, and a product that showed that as a mild
 * yellow note would be helping somebody ruin their own delivery quietly.
 *
 * Three things are stated rather than implied, because each is a place a
 * screen like this normally overclaims.
 *
 * **This checks presence, not correctness.** A TXT record starting `v=spf1` is
 * what a lookup can establish; whether the chain resolves, whether the key
 * matches what the provider signs with, and whether a policy is enforced all
 * need the receiver's view of a real message.
 *
 * **`unknown` is not a failure.** It means the lookup did not complete, which
 * is a different answer from "the record is missing" — and treating them alike
 * sends somebody off to edit DNS that was already right.
 *
 * **A check is a moment, not a state.** DNS changes without telling anybody, so
 * the date of the last answer sits beside it rather than a tick that implies
 * it is still true.
 */
export function SendingDomainsPage({ client }: { readonly client: ApiClient }) {
  const domains = useSendingDomains({ client })
  const { state, problem, busy } = domains

  /*
   * A member and a viewer read this tab and change nothing on it.
   *
   * The tab stays open to them deliberately — `SettingsBoard` keeps it
   * readable so somebody can see why mail is failing without being able to
   * touch a record — but adding, removing and re-checking a domain are all
   * writes the server refuses, so none of the three is offered.
   */
  const mayWrite = useMay('domains', 'write')

  const [domain, setDomain] = useState('')
  const [selector, setSelector] = useState('')

  const add = useCallback(async () => {
    if (await domains.add(domain, selector)) {
      setDomain('')
      setSelector('')
    }
  }, [domains, domain, selector])

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          Mail from a domain without SPF and DKIM goes to spam. This reads the records that are
          published — it cannot tell you they are correct, only that they are there.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}

      {!mayWrite ? null : (
        <CreateCard
          submit="Add domain"
          disabled={busy || domain.trim() === ''}
          onSubmit={() => void add()}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="domain-name"
              label="Domain"
              type="text"
              autoComplete="off"
              value={domain}
              disabled={busy}
              hint="Just the domain — carbonweb.co, not an address or a URL."
              onChange={setDomain}
            />
            <Field
              id="domain-selector"
              label="DKIM selector"
              type="text"
              autoComplete="off"
              value={selector}
              disabled={busy}
              optional
              hint="Given to you by whoever sends your mail. Add it later if you do not have it yet."
              onChange={setSelector}
            />
          </div>
        </CreateCard>
      )}

      <ListScreen
        state={state}
        onRetry={() => void domains.retry()}
        head={['Domain', 'SPF', 'DKIM', 'DMARC', 'Checked', '']}
        minWidth={840}
        empty={
          <EmptyCard heading="No domains yet">
            Add the domain your From address is on. Until its records are published, mailbox
            providers have no reason to trust mail claiming to come from it.
          </EmptyCard>
        }
        row={(domain) => (
          <Row
            key={domain.domain}
            domain={domain}
            busy={busy}
            mayWrite={mayWrite}
            onCheck={domains.check}
            onRemove={domains.remove}
          />
        )}
      />
    </>
  )
}

/**
 * `unknown` is its own tone, never the danger one.
 *
 * The same rule the send log follows for an ambiguous send: painting "we could
 * not ask" red asserts something the system does not know.
 */
const STATES = {
  found: { label: 'found', tone: 'ok' },
  missing: { label: 'missing', tone: 'danger' },
  unknown: { label: 'not checked', tone: 'unknown' },
} as const

function Row({
  domain,
  busy,
  mayWrite,
  onCheck,
  onRemove,
}: {
  readonly domain: SendingDomainDto
  readonly busy: boolean
  /** Both buttons here are writes, so neither is drawn without it. */
  readonly mayWrite: boolean
  readonly onCheck: (domain: string) => Promise<void>
  readonly onRemove: (domain: string) => Promise<void>
}) {
  // Which rows are expanded is about this screen and nothing else, so it stays
  // here rather than in the view model.
  const [showing, setShowing] = useState(false)

  return (
    <Tr>
      <Td strong>
        {domain.domain}
        {/* The verdict, said once and plainly. SPF and DKIM together are what a
            mailbox provider actually requires; DMARC is reported beside them
            and not counted. */}
        {domain.checkedAt === null ? null : (
          <span className="ml-2">
            <StatusPill tone={domain.ready ? 'ok' : 'danger'}>
              {domain.ready ? 'ready to send' : 'not ready'}
            </StatusPill>
          </span>
        )}
      </Td>
      <Td>
        <StatusPill tone={STATES[domain.spf].tone}>{STATES[domain.spf].label}</StatusPill>
      </Td>
      <Td>
        {domain.dkimSelector === '' ? (
          <span className="text-[12px] text-t3">no selector yet</span>
        ) : (
          <StatusPill tone={STATES[domain.dkim].tone}>{STATES[domain.dkim].label}</StatusPill>
        )}
      </Td>
      <Td>
        <StatusPill tone={STATES[domain.dmarc].tone}>{STATES[domain.dmarc].label}</StatusPill>
      </Td>
      {/* The date, not a tick. DNS changes without telling anybody. */}
      <Td>{domain.checkedAt === null ? 'never' : when(domain.checkedAt)}</Td>
      <Td>
        <span className="flex gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowing(!showing)}
            className="font-medium text-[13px] text-brand"
          >
            {showing ? 'Hide records' : 'Records to add'}
          </button>
          {/* Seeing which records are published is a read, so it stays. */}
          {!mayWrite ? null : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onCheck(domain.domain)}
                className="font-medium text-[13px] text-brand disabled:opacity-60"
              >
                Check now
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRemove(domain.domain)}
                className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
              >
                Remove
              </button>
            </>
          )}
        </span>
        {showing ? <Records records={domain.records} /> : null}
      </Td>
    </Tr>
  )
}

/**
 * What to publish, and where each value comes from.
 *
 * Inside the row rather than on a screen of its own: somebody reading "missing"
 * wants the fix in the same glance, and a second page to navigate to is a page
 * people do not find.
 *
 * DKIM has no value here and never will — the key is generated by whoever sends
 * your mail and exists only in their dashboard. The note says so rather than
 * leaving a blank that reads as a bug.
 */
function Records({ records }: { readonly records: readonly DnsRecordDto[] }) {
  return (
    <span className="mt-3 flex flex-col gap-3">
      {records.map((record) => (
        <span key={record.kind} className="block">
          <span className="block font-semibold text-[12px] text-t2">
            {record.kind} · {record.type} · {record.name}
          </span>
          {record.value === null ? null : (
            <span className="mt-1 block">
              <CopyableValue value={record.value} />
            </span>
          )}
          <span className="mt-1 block text-[12px] text-t3">{record.note}</span>
        </span>
      ))}
    </span>
  )
}
